import React, { useEffect, useState } from 'react';
import { PagingQuery } from 'tea-component';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import { sortTableByMetric } from '@/pages/intelligence/utils/tableUtils';
import { MetricsTableCloums } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/MetricsTableCloums';
import { DateColumns } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/DateCloums';
import { CountriesShareColumn } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/CountriesShareCloums';
import TopchartTable from '@/pages/intelligence/components/TableComponents/TopchartTable';
import { pageAble } from '@/pages/intelligence/utils/commonUtils';
import { RankCloums } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/RankCloums';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';

import type { IMetricExplanationTipsClickHandler } from '@intelligence/v3/components/MetricExplanationTips';

interface DynamicColTableProps {
  tableData: any[];
  dateType: string;
  startDate?: string;
  endDate?: string;
  pageSortAttr: any;
  columnList: any[];
  firstCol: any[];
  specialCol?: any[];
  getMetricValueOfCvm: (cvm: any, metricKey: string) => any;
  growthHasPoint?: string;
  pageSizeOptions: number[];
  defaultPageSize: number;
  fixedTableData?: any[];
  tableSortMetricProperty: string;
  tableSortGrowthProperty: string;
  localizedCountryName?: Function;
  loading?: boolean;
  sumAvgList?: any[];
  regionsCountries?: any[];
  entityType?: string;
  gameName?: string;
  gameCover?: string;
  onMetricExplanationClick?: IMetricExplanationTipsClickHandler;
}
const DynamicColTable = (props: DynamicColTableProps) => {
  const {
    tableData = [],
    dateType,
    startDate,
    endDate,
    pageSortAttr: defaultPageSortAttr,
    columnList,
    firstCol,
    specialCol,
    getMetricValueOfCvm = () => {},
    growthHasPoint,
    // pageSizeOptions = [5, 10, 15],
    pageSizeOptions = [10, 20, 30, 50, 100, 200],
    defaultPageSize = 5,
    fixedTableData,
    tableSortMetricProperty,
    tableSortGrowthProperty,
    localizedCountryName,
    loading = false,
    sumAvgList,
    regionsCountries,
    entityType,
    gameName,
    gameCover,
    onMetricExplanationClick,
  } = props;

  const tableTotal =
    (tableData?.length || 0) + (fixedTableData?.length || 0) + (sumAvgList?.length || 0);
  const [pageSortTable, setPageSortTable] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const { formatMessage } = useIntlExtend();
  const [sortTableData, setSortTableData] = useState(tableData);
  const [pageSortAttr, setPageSortAttr] = useState(defaultPageSortAttr);

  const {
    // tablePageIndex,
    // tablePageSize,
    currentSortValue,
    currentSortMetric,
    currentRatioType,
  } = pageSortAttr || {};

  // 初始化排序
  useEffect(() => {
    onSortChage({
      sortMetric: currentSortMetric,
      sortVal: 'desc',
      ratioType: 0,
    });
  }, [JSON.stringify(tableData), JSON.stringify(sumAvgList), defaultPageSortAttr]);

  const combineTable = (pageList: any[]) => {
    return [...(fixedTableData || []), ...(pageList || []), ...(sumAvgList || [])];
  };

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || defaultPageSize);
    // onTablePagingChange(e);
    const pageList = pageAble(sortTableData, e.pageIndex || 1, e.pageSize || defaultPageSize);
    setPageSortTable(combineTable(pageList));
  };

  // Others 行不论排序方向如何，始终固定在排序结果末尾
  const moveOthersToEnd = (list: any[]): any[] => {
    if (!Array.isArray(list) || list.length === 0) return list || [];
    const isOthersRow = (m: any) =>
      typeof m === 'string' && m.toLowerCase() === 'others';
    const others = list.filter((item: any) => isOthersRow(item?.market));
    if (others.length === 0) return list;
    const nonOthers = list.filter((item: any) => !isOthersRow(item?.market));
    return [...nonOthers, ...others];
  };

  const onSortChage = (e: SortByItemType) => {
    setPageSortAttr({
      currentSortValue: e.sortVal,
      currentSortMetric: e.sortMetric,
      currentRatioType: e.ratioType,
    });
    const sortTable = moveOthersToEnd(
      sortTableByMetric({
        tableData,
        isRank: e.ratioType === 0 && e.sortMetric === 'Rank', // 是否是排序指标，暂处理MAU的Rank
        sortParams: e,
        metricProperty: tableSortMetricProperty,
        growthProperty: tableSortGrowthProperty,
      }),
    );

    const pageList: any[] = pageAble(sortTable, pageIndex, pageSize);
    // setPageSortTable([...(fixedTableData || []), ...(pageList || [])]);
    setPageSortTable(combineTable(pageList));
    setSortTableData(sortTable.slice(0));
  };
  const getMetricColumns = () => {
    const metricColumns: any[] = [];
    // console.log(columnList)
    columnList?.forEach((t) => {
      metricColumns.push(
        MetricsTableCloums({
          /** MetricsTable页只有降序功能 */
          onlySimpleSort: false,
          renderType: t?.renderType,
          width: t?.width,
          width_cn: t?.width_cn,
          icon: t?.icon,
          dataSource: t?.dataSource, // source === 'ogdb' ? 'databarin' : source,
          bubbleDataSource: t?.bubbleDataSource, // 气泡提示显示的数据源
          title: /* t?.textDetail ??*/ t?.text,
          textContent: t?.infoDetail ?? t?.info,
          textContentSuffix: t?.infoSuffix,
          tipText: t?.iconInfo,
          key: `${t?.value}`,
          sortMetric: t?.value,
          currentSortValue,
          currentSortKey: `${currentSortMetric}_${currentRatioType}`,
          onSortChage,
          dateType,
          startDate,
          endDate,
          getMetricValueOfCvm,
          formatMessage,
          growthHasPoint,
          isNoDropdown: tableTotal === 1, // 只有一条数据时不展示排序图标
          isDetailPage: true,
          dataSourceType: t?.dataSourceType || t?.value,
          entityType,
          gameName,
          gameCover,
          svgTipIsNewMore: t?.newMore,
          onMetricExplanationClick,
        }),
      );
    });
    return metricColumns;
  };

  const getFirstColumns = (config: any[]) => {
    if (!Array.isArray(config)) {
      return [];
    }
    const firstColumns = config?.map((col) => {
      const { type, key, sortMetric, width, title, sumInfo } = col;
      switch (type) {
        case 'date':
          return DateColumns({
            key,
            sortMetric,
            title: formatMessage({ id: title }),
            currentSortMetric,
            currentSortValue,
            dateType,
            onSortChage,
            width,
            sumInfo: sumInfo ? formatMessage({ id: sumInfo }) : '',
          });
        case 'countries':
          return CountriesShareColumn({
            // countriesShareType: regionSegVal,
            localizedCountryName,
            regionsCountries,
            showOthersCountryFlag: true,
          });
        default:
          return { width: 0 };
      }
    });
    return firstColumns;
  };

  const getSpecialColumns = (config?: any[]) => {
    if (!Array.isArray(config)) {
      return [];
    }
    const specialColumns = config?.map((col) => {
      const { type } = col;
      switch (type) {
        case 'rank':
          return RankCloums({
            onlySimpleSort: false,
            title: col?.text,
            key: col?.value,
            sortMetric: col?.value,
            currentSortValue,
            currentSortKey: `${currentSortMetric}_${currentRatioType}`,
            textContent: col?.info,
            tipText: col?.iconInfo,
            onSortChage,
            width: col?.width || 130,
            getMetricValueOfCvm,
            renderType: col?.renderType,
          });
        default:
          return { width: 0 };
      }
    });
    return specialColumns;
  };
  const getAllColumns = (): any[] => {
    // 去掉空白列
    return [...getFirstColumns(firstCol), ...getMetricColumns(), ...getSpecialColumns(specialCol)];
  };

  return (
    <>
      <TopchartTable
        // recordKey="market"
        className="dbi-mobileGames-details-table"
        loading={loading}
        records={pageSortTable}
        tableTotal={tableTotal}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        pageSizeOptions={pageSizeOptions}
        // scrollMaxHeight={'calc(100vh - 290px)'}
        columns={getAllColumns()}
      />
    </>
  );
};

export default DynamicColTable;
